import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Hls from 'hls.js';
import '../css/recordings.css';
import { STREAM_API, getToken, apiFetch } from '../utils/streamApi';
import { requestFullscreen, exitFullscreen, onFullscreenExit } from '../utils/fullscreen';
import useIsMobile from '../utils/useIsMobile';
import PlaybackSpeedButton from '../components/PlaybackSpeedButton';

const pad         = n => String(n).padStart(2, '0');
const fmtLocalDate = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
const localToday  = () => fmtLocalDate(new Date());
// Defaulting the time filter to the start of the day pulled in the whole
// day's recordings on every page load — default to an hour before landing
// on the page instead, computed together with the date so this doesn't
// break at the edge case of loading the page just after midnight (where
// "an hour ago" is actually still on the previous day).
const oneHourAgoDateTime = () => {
  const d = new Date(Date.now() - 60 * 60 * 1000);
  return { date: fmtLocalDate(d), time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
};
// Load only what was asked for: the recordings list starts at the 15-min chunk
// holding the chosen time (10:05 → 10:00, the chunk the server groups it in) and
// only reaches further back when the user actually goes there — Go / ‹ / a tap
// on the timeline before the loaded part. The whole day was the default before
// and was most of the /folder-recordings traffic.
const SEG_SECS    = 15 * 60;
const hhmmToSecs  = t => { const [h, m] = (t || '00:00').split(':').map(Number); return (h || 0) * 3600 + (m || 0) * 60; };
const windowStart = sec => Math.max(0, sec - (sec % SEG_SECS));
const hhmmss      = sec => `${pad(Math.floor(sec / 3600))}${pad(Math.floor((sec % 3600) / 60))}${pad(sec % 60)}`;
// "HH:MM" or "HH:MM:SS" (a time input with step=1) → second of day; null if not a time.
const timeToSecs  = t => {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(t || '');
  if (!m || +m[1] > 23 || +m[2] > 59 || +(m[3] || 0) > 59) return null;
  return +m[1] * 3600 + +m[2] * 60 + +(m[3] || 0);
};
// A browsing range (PRVSK-78) is one day, at most this long — longer ones load slowly.
const MAX_RANGE_SECS = 6 * 3600;

// Coalesce a burst of camera toggles into a single recordings load.
const SELECTION_DEBOUNCE_MS = 350;
// Selected cameras all play at once in the grid, and each HLS player pulls
// ~3 MB of initial buffer then ~107 KB/s of segments. Past a handful that
// saturates a remote link long before it troubles the server, so cap it.
const MAX_CONCURRENT_PLAYBACK = 4;
// HLS network-error retry: exponential from 1s, giving up after 5 tries (~31s).
const HLS_NET_RETRY_MS    = 1000;
const HLS_NET_MAX_RETRIES = 5;
// Media-error recovery attempts before giving up. recoverMediaError() resumes
// from the same position, so a segment the decoder can never accept must be
// capped or it is retried forever — see the ERROR handler.
const HLS_MEDIA_MAX_RECOVERIES = 2;
// How long to wait before retrying while the recordings index is building, and
// how many times. Capped because "building" is not always transient: if the
// recordings directory is missing or unreadable the API keeps answering 503,
// and an uncapped retry would poll it forever.
const INDEX_RETRY_MS  = 4000;
const INDEX_RETRY_MAX = 15;        // ~1 minute, then report it
const fmtSecs    = s => { s = Math.max(0, Math.floor(s)); return `${pad(Math.floor(s/3600))}:${pad(Math.floor((s%3600)/60))}:${pad(s%60)}`; };
const fmtDT      = dt => {
  if (!dt || dt.length < 15) return dt || '—';
  return `${dt.slice(6,8)}/${dt.slice(4,6)}/${dt.slice(0,4)}  ${dt.slice(9,11)}:${dt.slice(11,13)}`;
};
const fmtDateNav = str => { const d = new Date(str + 'T00:00:00'); return d.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }); };
const dateFromRec   = rec => rec.datetime ? rec.datetime.slice(0,8) : '';
const recStartSecs  = rec => {
  const dt = rec.datetime;
  if (!dt || dt.length < 15) return 0;
  return parseInt(dt.slice(9,11)) * 3600 + parseInt(dt.slice(11,13)) * 60 + parseInt(dt.slice(13,15));
};

function mapCam(cam, idx, device) {
  return { idx, id: cam.name.toLowerCase().replace(/\s+/g, '-'), name: cam.name.replace(/\b\w/g, c => c.toUpperCase()), zone: cam.zone || '', ip: cam.ip || device?.ip || '' };
}

// ─── HLS DVR Player ───────────────────────────────────────────────────────────
function HlsVideoPlayer({ camIdx, token, fromDt, toDt, offset, className, onTimeUpdate, onEnded, speed, onSpeed }) {
  const videoRef = useRef(null);
  const hlsRef   = useRef(null);
  const [buffering, setBuffering] = useState(true);

  useEffect(() => {
    if (!token || camIdx == null || !fromDt || !videoRef.current) return;
    setBuffering(true);
    let netRetries = 0;
    let mediaRecoveries = 0;
    let retryTimer = null;
    const url = `${STREAM_API}/hls/${camIdx}/dvr.m3u8?from_dt=${fromDt}&to_dt=${toDt || fromDt}&token=${token}`;

    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: false, maxBufferLength: 30, fragLoadingMaxRetry: 2, fragLoadingRetryDelay: 1000 });
      hlsRef.current = hls;
      hls.loadSource(url);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = true;                              // required for autoplay
        v.playsInline = true;                        // play inline, don't pop to fullscreen
        v.setAttribute('playsinline', '');
        v.setAttribute('webkit-playsinline', '');    // legacy iOS Safari
        if (offset > 0) v.currentTime = offset;
        v.play().catch(() => {});
      });
      // Network errors are retried with backoff, not immediately. The old
      // handler called startLoad() straight away and skipped 5s forward each
      // time, so a playlist returning 503 while the recordings index builds
      // after a restart produced hundreds of requests in seconds and appeared
      // to hang. Skipping forward is also only right for a bad *fragment* — on
      // a manifest error it just loses position for no reason.
      hls.on(Hls.Events.FRAG_LOADED, () => { netRetries = 0; });
      // A media error counts as recovered only once a fragment reaches the
      // buffer. FRAG_LOADED would be the wrong signal and would uncap the
      // counter entirely: in the failure below the fragments load fine — 200,
      // usually straight from disk cache — and it is the append that fails.
      hls.on(Hls.Events.FRAG_BUFFERED, () => { mediaRecoveries = 0; });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (!data.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          if (netRetries >= HLS_NET_MAX_RETRIES) {
            hls.destroy(); hlsRef.current = null; setBuffering(false);
            return;
          }
          const delay = HLS_NET_RETRY_MS * Math.pow(2, netRetries);
          netRetries += 1;
          if (String(data.details || '').toLowerCase().includes('frag')
              && videoRef.current) {
            videoRef.current.currentTime += 5;   // skip a bad segment
          }
          retryTimer = setTimeout(() => { try { hls.startLoad(); } catch { /* destroyed */ } }, delay);
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          // recoverMediaError() resumes from the same position, so a segment the
          // decoder can never accept would be fetched again, fail again, and
          // recover to the same spot forever — thousands of requests in a
          // minute with the player frozen at its start time. Older recordings
          // can still contain such segments (recorder cut on a timer rather
          // than on keyframes), so advance past the offending fragment before
          // recovering: one bad segment then costs a moment, not the whole
          // tile. Escalate only if recovery keeps failing regardless.
          if (mediaRecoveries >= HLS_MEDIA_MAX_RECOVERIES) {
            hls.destroy(); hlsRef.current = null; setBuffering(false);
            return;
          }
          mediaRecoveries += 1;
          if (mediaRecoveries === 2) hls.swapAudioCodec();
          const frag = data.frag;
          if (frag && videoRef.current) {
            videoRef.current.currentTime = frag.start + frag.duration + 0.05;
          }
          hls.recoverMediaError();
        } else {
          hls.destroy(); hlsRef.current = null;
        }
      });
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      const v = videoRef.current;
      v.muted = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      v.setAttribute('webkit-playsinline', '');
      v.src = url;
      v.addEventListener('loadedmetadata', () => {
        if (offset > 0) v.currentTime = offset;
        v.play().catch(() => {});
      }, { once: true });
    }

    return () => {
      // Drop any pending retry first — otherwise it fires against a destroyed
      // instance, and a seek that recreates the player would leave the old
      // one's timer still loading.
      clearTimeout(retryTimer);
      if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; }
      if (videoRef.current) videoRef.current.src = '';
    };
  }, [camIdx, token, fromDt, toDt, offset]); // eslint-disable-line

  return (
    <>
      <video ref={videoRef} muted playsInline className={className}
        onPlaying={() => setBuffering(false)}
        onWaiting={() => setBuffering(true)}
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded} />
      {buffering && <div className="rec-tile-loading"><div className="rec-spinner" /></div>}
      <PlayerControlBar videoRef={videoRef} speed={speed} onSpeed={onSpeed} />
    </>
  );
}

// ─── Persistent play/pause + position + mute control bar ─────────────────────
// Native <video controls> auto-hide during playback and offer no way to tell
// at a glance whether a tile is muted, so this stays visible the whole time
// rather than relying on hover/tap-to-reveal chrome.
function PlayerControlBar({ videoRef, speed, onSpeed, mediaKey }) {
  const [playing, setPlaying] = useState(true);
  const [muted,   setMuted]   = useState(true);
  const [time,    setTime]    = useState(0);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay   = () => setPlaying(true);
    const onPause  = () => setPlaying(false);
    const onVolume = () => setMuted(v.muted);
    const onTime   = () => setTime(v.currentTime);
    setPlaying(!v.paused);
    setMuted(v.muted);
    setTime(v.currentTime);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('volumechange', onVolume);
    v.addEventListener('timeupdate', onTime);
    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('volumechange', onVolume);
      v.removeEventListener('timeupdate', onTime);
    };
  }, [videoRef]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (v) v.muted = !v.muted;
  };

  return (
    <div className="rec-player-controls">
      <button className="rec-player-ctrl-btn" onClick={togglePlay} title={playing ? 'Pause' : 'Play'}>
        {playing ? '⏸' : '▶'}
      </button>
      <span className="rec-player-ctrl-time">{fmtSecs(time)}</span>
      {onSpeed && <PlaybackSpeedButton className="rec-player-ctrl-btn" videoRef={videoRef}
        speed={speed} onChange={onSpeed} mediaKey={mediaKey} />}
      <button className="rec-player-ctrl-btn" onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'}>
        {muted ? '🔇' : '🔊'}
      </button>
    </div>
  );
}

// ─── Timeline Bar ─────────────────────────────────────────────────────────────
// The timeline shows the requested part of the day — loaded range plus a
// margin each side — not all 24h: on a phone an hour of a full-day bar is a
// sliver too thin to tap. "Full day" switches back to 00:00–24:00.
const TL_MARGIN   = 15 * 60;       // padding each side of the data
const TL_AHEAD    = 2 * 3600;      // show at most this far past the chosen time / playhead
                                   // (a past day is loaded to 24:00 — don't show all of it)
const tsToSecs    = ts => (ts && ts.length >= 15)
  ? parseInt(ts.slice(9,11)) * 3600 + parseInt(ts.slice(11,13)) * 60 + parseInt(ts.slice(13,15)) : null;
const nowSecOfDay = () => { const d = new Date(); return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds(); };
const tickStep    = span => span <= 1.5 * 3600 ? 900 : span <= 3 * 3600 ? 1800 : span <= 8 * 3600 ? 3600 : span <= 14 * 3600 ? 7200 : 10800;

function TimelineBar({ recs, onSeek, currentPos, emptyLabel, inSec, outSec, loadedFrom = 0, nowSec = null,
                      rangeFrom = null, rangeTo = null }) {
  const DAY    = 86400;
  const [fullDay, setFullDay] = useState(false);
  const [hover, setHover] = useState(null);   // { x, w, sec } cursor/drag preview
  const draggingRef = useRef(false);
  const sorted = [...recs].sort((a, b) => recStartSecs(a) - recStartSecs(b));
  const limit  = nowSec ?? DAY;                     // today: nothing after "now"
  // A chunk ends at its last segment (datetime_end), not a fixed 15 min — the
  // current chunk used to be drawn up to 15 min into the future.
  const segEnd = (rec, i) => {
    const start = recStartSecs(rec);
    const next  = i < sorted.length - 1 ? recStartSecs(sorted[i+1]) : start + 15 * 60;
    const last  = tsToSecs(rec.datetime_end);
    const end   = last != null ? Math.min(next, Math.max(start + 1, last + 2)) : next;
    return Math.min(end, Math.max(start + 1, limit));
  };

  // Visible range: everything loaded/marked/playing, TL_MARGIN each side.
  let viewStart = 0, viewEnd = DAY;
  if (!fullDay && rangeTo != null) {
    // A start → end range was asked for (PRVSK-78): show exactly that window,
    // widened only if the playhead or a clip mark sits outside it.
    viewStart = Math.max(0, Math.min(rangeFrom ?? loadedFrom, currentPos ?? Infinity, inSec ?? Infinity));
    viewEnd   = Math.min(DAY, Math.max(rangeTo, currentPos ?? 0, outSec ?? 0));
  } else if (!fullDay) {
    const dataEnd = sorted.length ? Math.max(...sorted.map(segEnd)) : Math.min(limit, loadedFrom + 3600);
    const lo = Math.min(loadedFrom, currentPos ?? Infinity, inSec ?? Infinity);
    const focus = Math.max(loadedFrom, currentPos ?? 0);
    const hi = Math.max(Math.min(dataEnd, focus + TL_AHEAD), currentPos ?? 0, outSec ?? 0);
    viewStart = Math.max(0, lo - TL_MARGIN);
    viewEnd   = Math.min(DAY, hi + TL_MARGIN);
  }
  const span  = Math.max(1, viewEnd - viewStart);
  const pct   = sec => ((sec - viewStart) / span) * 100;
  const step  = fullDay ? 10800 : tickStep(span);
  const ticks = [];
  for (let t = Math.ceil(viewStart / step) * step; t <= viewEnd; t += step) ticks.push(t);

  const secAt = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x    = Math.min(rect.width, Math.max(0, e.clientX - rect.left));
    return { x, w: rect.width, sec: Math.min(DAY - 1, Math.floor(viewStart + (x / rect.width) * span)) };
  };

  // Pointer events unify mouse + touch so the bar is draggable on mobile.
  // Drag shows a live preview; the actual seek fires on release (tap = seek too).
  const onDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setHover(secAt(e));
  };
  const onMove = (e) => {
    if (draggingRef.current || e.pointerType === 'mouse') setHover(secAt(e));
  };
  const onUp = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    onSeek(secAt(e).sec);
    if (e.pointerType !== 'mouse') setHover(null);   // hide preview after touch
  };
  const onLeave = (e) => {
    if (e.pointerType === 'mouse' && !draggingRef.current) setHover(null);
  };

  const unloadedEnd = Math.min(loadedFrom, viewEnd);
  return (
    <div className="rec-timeline-wrap">
      {/* Zoom toggle sits beside the track, not on a row of its own */}
      <div className="rec-timeline-row">
      <div className="rec-timeline-main">
      <div className="rec-timeline-labels">
        {ticks.map(t => {
          const p = pct(t);
          // Keep edge labels inside the track instead of clipping.
          const tf = p < 4 ? 'none' : p > 96 ? 'translateX(-100%)' : 'translateX(-50%)';
          return <span key={t} style={{ left: `${p}%`, transform: tf }}>{fmtSecs(t).slice(0, 5)}</span>;
        })}
      </div>
      <div className="rec-timeline-track"
        style={{ touchAction: 'none' }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onPointerLeave={onLeave}
      >
        {unloadedEnd > viewStart && (
          <div className="rec-timeline-unloaded" style={{ width: `${pct(unloadedEnd)}%` }} />
        )}
        {sorted.map((rec, i) => {
          const a = Math.max(recStartSecs(rec), viewStart);
          const b = Math.min(segEnd(rec, i), viewEnd);
          if (b <= a) return null;
          return (
            <div key={rec.filename} className="rec-timeline-block"
              style={{ left: `${pct(a)}%`, width: `${pct(b) - pct(a)}%` }}
              title={fmtDT(rec.datetime)}
            />
          );
        })}
        {/* Export in/out selection: shaded band + green in / red out markers */}
        {inSec != null && outSec != null && outSec > inSec && (
          <div style={{ position:'absolute', top:0, bottom:0, left:`${pct(inSec)}%`,
            width:`${pct(outSec) - pct(inSec)}%`, background:'rgba(80,160,255,0.28)',
            borderLeft:'2px solid #2ecc71', borderRight:'2px solid #e74c3c', pointerEvents:'none' }} />
        )}
        {inSec != null && outSec == null && (
          <div style={{ position:'absolute', top:0, bottom:0, left:`${pct(inSec)}%`,
            width:2, background:'#2ecc71', pointerEvents:'none' }} />
        )}
        {currentPos != null && (
          <div className="rec-timeline-needle" style={{ left: `${pct(currentPos)}%` }} />
        )}
        {hover && (
          <>
            <div className="rec-timeline-hover-line" style={{ left: hover.x }} />
            <div className="rec-timeline-hover-tip"
              style={{ left: Math.min(Math.max(hover.x, 30), hover.w - 30) }}>
              {fmtSecs(hover.sec)}
            </div>
          </>
        )}
        {emptyLabel && <div className="rec-timeline-empty-label">{emptyLabel}</div>}
      </div>
      </div>
      <button type="button" className="rec-timeline-zoom" onClick={() => setFullDay(f => !f)}
              title={fullDay ? 'Zoom to the loaded time' : 'Show the whole day (00:00–24:00)'}
              aria-label={fullDay ? 'Zoom timeline to the loaded time' : 'Show the whole day on the timeline'}>
        {fullDay ? 'Zoom' : '24h'}
      </button>
      </div>
    </div>
  );
}

// ─── Camera cell (shared between in-page grid and fullscreen portal) ─────────
function RecCamCell({ cam, seg, token, noPlayback, isHls, videoUrl, onTimeUpdate, onEnded, showExportPick, isExportPick, onPickExport, speed, onSpeed }) {
  const plainVideoRef = useRef(null);
  return (
    <div className="rec-cam-grid-cell" role="group" aria-label={cam.name}>
      <div className="rec-multicam-label">{cam.name}</div>
      {showExportPick && (
        <label className="rec-export-pick" title={`Export ${cam.name}`} onClick={e => e.stopPropagation()}>
          <input type="checkbox" checked={isExportPick} onChange={() => onPickExport(cam.idx)} />
          Export
        </label>
      )}
      {seg ? (
        <div style={{ position:'relative', width:'100%', height:'100%', background:'#000' }}>
          {isHls(seg.rec) ? (
            <HlsVideoPlayer
              key={seg.rec.datetime}
              camIdx={seg.rec.cam_idx}
              token={token}
              fromDt={seg.rec.datetime}
              toDt={seg.rec.datetime_end}
              offset={seg.offset}
              className="rec-multicam-video"
              onTimeUpdate={onTimeUpdate}
              onEnded={onEnded}
              speed={speed}
              onSpeed={onSpeed}
            />
          ) : (
            <>
              <video
                key={seg.rec.filename}
                ref={plainVideoRef}
                src={videoUrl(seg.rec)}
                autoPlay muted playsInline preload="metadata"
                className="rec-multicam-video"
                onLoadedMetadata={e => {
                  const v = e.target;
                  v.muted = true;
                  v.playsInline = true;
                  v.setAttribute('playsinline', '');
                  v.setAttribute('webkit-playsinline', '');
                  v.currentTime = seg.offset;
                }}
                onTimeUpdate={onTimeUpdate}
                onEnded={onEnded}
              />
              <PlayerControlBar videoRef={plainVideoRef} speed={speed} onSpeed={onSpeed} mediaKey={seg.rec.filename} />
            </>
          )}
        </div>
      ) : (
        <div className="rec-cam-no-recording">
          <div className="rec-no-rec-body">
            {noPlayback
              ? <div>No footage to play — try another time or date</div>
              : <div>No recording at this time</div>
            }
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Camera multi-select dropdown ─────────────────────────────────────────────
// singleSelect (mobile, PRVSK-37): radio-style, applied immediately on tap —
// there's only ever one camera in play, so there's nothing to stage/burst.
//
// Multi-select (desktop): checking/unchecking boxes only edits a local
// *draft* — nothing reaches the parent (and so no recordings API call fires)
// until OK is pressed (PRVSK-38). This stops a burst of individual
// tick/untick clicks from each kicking off its own superseded lookup.
function CamMultiSelect({ cameras, selected, onApply, max, singleSelect }) {
  const [open,  setOpen]  = useState(false);
  const [draft, setDraft] = useState(selected);
  const ref = useRef(null);

  // Every time the menu opens, start the draft from what's actually loaded —
  // discards any draft left over from a previous close-without-OK.
  useEffect(() => {
    if (open) setDraft(selected);
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (idx) => {
    if (singleSelect) {
      onApply(selected.size === 1 && selected.has(idx) ? new Set() : new Set([idx]));
      setOpen(false);
      return;
    }
    setDraft(prev => {
      const next = new Set(prev);
      if (next.has(idx)) { next.delete(idx); return next; }
      if (next.size >= max) return prev;
      next.add(idx);
      return next;
    });
  };

  const commit = () => { onApply(draft); setOpen(false); };

  const count = selected.size;   // trigger label always reflects what's loaded
  const atMax = !singleSelect && draft.size >= max;
  const label = count === 0            ? (singleSelect ? 'Select a camera…' : 'Select cameras…')
    : count === cameras.length          ? `All cameras (${count})`
    : count === 1                       ? (cameras.find(c => selected.has(c.idx))?.name || '1 camera')
    :                                     `${count} cameras`;

  return (
    <div className="rec-cam-select" ref={ref}>
      <button className={`rec-cam-select-btn${count ? ' has-sel' : ''}`}
        onClick={() => setOpen(o => !o)} title="Choose which camera to view">
        <span className="rec-cam-select-icon">📷</span>
        <span className="rec-cam-select-label">{label}</span>
        <span className="rec-cam-select-caret">▾</span>
      </button>
      {open && (
        <div className="rec-cam-select-menu">
          <div className="rec-cam-select-actions">
            {!singleSelect && (
              <button onClick={() => setDraft(new Set(cameras.slice(0, max).map(c => c.idx)))}>
                {cameras.length > max ? `Select first ${max}` : 'Select all'}
              </button>
            )}
            <button onClick={() => singleSelect ? onApply(new Set()) : setDraft(new Set())}
              disabled={(singleSelect ? selected.size : draft.size) === 0}>Clear</button>
          </div>
          {atMax && (
            <div className="rec-cam-select-hint">
              Showing the maximum of {max} cameras at once — clear one to pick another.
            </div>
          )}
          <div className="rec-cam-select-list">
            {cameras.map(cam => {
              const on = singleSelect ? selected.has(cam.idx) : draft.has(cam.idx);
              return (
                // Unchecked boxes are disabled at the cap rather than silently
                // ignoring the click, so the limit is visible before it bites.
                // (Single-select has no cap — every row stays tappable.)
                <label key={cam.id}
                  className={`rec-cam-select-item${!singleSelect && !on && atMax ? ' is-disabled' : ''}`}>
                  <input type={singleSelect ? 'radio' : 'checkbox'} name={singleSelect ? 'rec-cam-radio' : undefined}
                    checked={on} disabled={!singleSelect && !on && atMax}
                    onChange={() => toggle(cam.idx)} />
                  <span className="rec-cam-select-name">{cam.name}</span>
                  {cam.zone && <span className="rec-cam-select-zone">{cam.zone}</span>}
                </label>
              );
            })}
          </div>
          {/* Mobile applies each tap immediately and closes — no separate
              confirm needed with only one camera ever in play. */}
          {!singleSelect && (
            <button className="rec-cam-select-done" onClick={commit}>
              OK{draft.size ? ` (${draft.size})` : ''}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ManageRecordingsPage() {
  // PRVSK-37: Recordings allows only one camera at a time on mobile —
  // multi-camera grids don't fit a 375px viewport.
  const isMobile = useIsMobile();
  const [token,    setToken]    = useState(null);
  const [cameras,  setCameras]  = useState([]);
  const [initDone, setInitDone] = useState(false);

  const [recs,    setRecs]    = useState([]);
  const [loading, setLoading] = useState(false);
  // The recordings index builds on first use; while it does, the API answers
  // "index_building" and we show that instead of an empty list.
  const [indexing,    setIndexing]   = useState(false);   // false | true | 'stalled'
  const [indexRetry,  setIndexRetry] = useState(0);
  const indexRetryRef = useRef(null);   // pending retry timer
  const indexTriesRef = useRef(0);      // retries so far, reset on success//new query

  const [viewDate,     setViewDate]    = useState(() => oneHourAgoDateTime().date);
  const [viewTime,     setViewTime]    = useState(() => oneHourAgoDateTime().time); // HH:MM input value, defaults to 1h before page load
  // Second-of-day the loaded recordings list starts at — see windowStart above.
  const [loadFromSec,  setLoadFromSec] = useState(() => windowStart(hhmmToSecs(oneHourAgoDateTime().time)));
  // Where the timeline starts SHOWING — the chunk of the last chosen time. Can be
  // later than loadFromSec: going to 15:46 hides the already-loaded 15:30 chunk
  // (it stays in memory, so tapping back there shows it again with no request).
  // Traffic is governed by loadFromSec; this is presentation only.
  const [viewFromSec,  setViewFromSec] = useState(() => windowStart(hhmmToSecs(oneHourAgoDateTime().time)));
  // Browse a start → end range instead of one time (PRVSK-78). rangeOn shows the
  // end field ("+ end time"); appliedRange is what Go last loaded, so editing the
  // fields doesn't move the timeline until Go.
  const [rangeOn,      setRangeOn]      = useState(false);
  const [viewToTime,   setViewToTime]   = useState('');
  const [appliedRange, setAppliedRange] = useState(null);   // { from, to } seconds of day
  const [currentSegs,  setCurrentSegs] = useState([]);     // [{cam, rec, offset}]
  const [playbackPos,  setPlaybackPos] = useState(null);
  // One speed for every tile, so a multi-camera view stays in step.
  const [playSpeed,    setPlaySpeed]   = useState(1);
  // Set of cam.idx the customer chose to view — always starts empty so the
  // user must pick a camera each visit (no auto-selection).
  const [selectedCams, setSelectedCams] = useState(() => new Set());
  const [isFs,         setIsFs]         = useState(false);
  // Clip export: mark in/out (seconds-of-day) on the timeline, then download.
  const [clipIn,   setClipIn]   = useState(null);
  const [clipOut,  setClipOut]  = useState(null);
  const [exportMax, setExportMax] = useState(10);   // minutes, from storage settings
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState(null);
  // Which camera's tile is checked for export — required when more than one
  // camera is visible (PRVSK-32/PRVSK-37: exporting used to silently default
  // to the first selected camera with no way to choose another).
  const [exportCamIdx, setExportCamIdx] = useState(null);
  useEffect(() => {
    apiFetch('/settings/storage').then(r => r.json())
      .then(d => { if (d && d.export_max_minutes) setExportMax(d.export_max_minutes); })
      .catch(() => {});
  }, []);
  const timeEditingRef = useRef(false);   // true while user is editing the time field
  // A time the user picked but hasn't applied yet (Go / Enter). Playback must not
  // overwrite it: leaving the field used to snap it straight back to the playhead
  // time (picked 3:35, saw 3:31 again), so Go then played the old time.
  const timeDirtyRef   = useRef(false);
  const lastAdvanceRef = useRef(0);       // debounce auto-advance across cameras

  // Keep the time field in step with the playback position (timeline needle),
  // except while the user is actively editing it.
  useEffect(() => {
    if (playbackPos == null || timeEditingRef.current || timeDirtyRef.current || rangeOn) return;
    const hhmm = `${pad(Math.floor(playbackPos / 3600))}:${pad(Math.floor((playbackPos % 3600) / 60))}`;
    setViewTime(prev => prev === hhmm ? prev : hhmm);
  }, [playbackPos, rangeOn]);

  const today   = localToday();
  const isToday = viewDate === today;

  // ── auth + cameras ────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const tok = await getToken();
        if (!tok) return;
        setToken(tok);
        const r = await apiFetch('/cameras');
        if (!r.ok) return;
        const d = await r.json();
        setCameras((d.cameras || []).map(c => mapCam(c, c.idx, d.device)));
      } catch {}
      finally { setInitDone(true); }
    })();
  }, []);

  // ── fetch recordings on the selected date, only for the chosen cameras ────
  // The recordings index is built on first use, so the very first load after a
  // restart can come back "still indexing" instead of data. That is a normal,
  // self-resolving state — surface it as a message and retry, not as an error.
  const fetchRecs = async (params, signal) => {
    const r = await apiFetch(`/folder-recordings?${params}`, { signal });
    if (r.status === 503) {
      const body = await r.json().catch(() => null);
      if (body?.detail?.code === 'index_building') {
        const e = new Error(body.detail.message); e.name = 'IndexBuilding'; throw e;
      }
    }
    const data = await r.json();
    return data.recordings || [];
  };

  const loadRecs = useCallback(async (date, tok, camIdxs, total, signal, fromSec = 0, toSec = null) => {
    if (!tok || !camIdxs.length) { setRecs([]); return; }
    setLoading(true);
    try {
      const yyyymmdd = date.replace(/-/g, '');
      // A browsing range loads only up to its end; otherwise the rest of the day.
      const base = { from_dt: `${yyyymmdd}_${hhmmss(fromSec)}`,
                     to_dt: `${yyyymmdd}_${toSec != null ? hhmmss(toSec) : '235959'}`, page: 0 };
      // Most/all cameras selected → one unfiltered scan is lighter than one
      // filtered scan per camera; a small subset → targeted per-camera fetches.
      const useUnfiltered = camIdxs.length >= Math.max(2, Math.ceil(total * 0.6));
      let recsList;
      if (useUnfiltered) {
        const want   = new Set(camIdxs);
        const params = new URLSearchParams({ ...base, per_page: Math.max(500, total * 150) });
        recsList     = (await fetchRecs(params, signal)).filter(rec => want.has(rec.cam_idx));
      } else {
        const lists = await Promise.all(camIdxs.map(idx =>
          fetchRecs(new URLSearchParams({ ...base, per_page: 500, cam_idx: idx }), signal)));
        recsList = lists.flat();
      }
      setRecs(recsList);
      setIndexing(false);
      indexTriesRef.current = 0;
    } catch (e) {
      // An aborted load has been superseded by a newer selection — leave the
      // existing rows and the loading flag to the request that replaced it.
      if (e?.name === 'AbortError') return;
      if (e?.name === 'IndexBuilding') {
        // Not an error — the index is building. Show it and come back, but stop
        // after INDEX_RETRY_MAX: a missing recordings directory 503s forever.
        // The counter is a ref, not state, so retrying cannot itself re-render.
        setRecs([]);
        if (indexTriesRef.current >= INDEX_RETRY_MAX) { setIndexing('stalled'); return; }
        indexTriesRef.current += 1;
        setIndexing(true);
        indexRetryRef.current = setTimeout(() => setIndexRetry(v => v + 1), INDEX_RETRY_MS);
        return;
      }
      setIndexing(false);
      setRecs([]);
    }
    finally { if (!signal?.aborted) setLoading(false); }
  }, []);

  // Only load once the user has picked camera(s); reload when the date or the
  // selection changes. Nothing is fetched on the bare page (no camera = no load).
  //
  // Camera toggles arrive one at a time and each one re-requests every camera
  // already selected, so picking 5 cameras used to fire 1+2+3+4+5 = 15
  // overlapping /folder-recordings calls with nothing cancelling the superseded
  // ones. Each of those is an expensive server-side scan, so they pile up.
  // Debounce so a burst of toggles coalesces into one load, and abort whatever
  // is still in flight when a newer selection replaces it.
  const loadAbortRef = useRef(null);
  useEffect(() => {
    const idxs = [...selectedCams];
    if (!token || !idxs.length) { setRecs([]); setIndexing(false); return; }
    // A pending index retry is superseded by this run.
    clearTimeout(indexRetryRef.current);
    const timer = setTimeout(() => {
      loadAbortRef.current?.abort();
      const ac = new AbortController();
      loadAbortRef.current = ac;
      loadRecs(viewDate, token, idxs, cameras.length, ac.signal, loadFromSec, appliedRange?.to ?? null);
    }, SELECTION_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // indexRetry is a deliberate re-trigger while the index builds.
  }, [token, viewDate, selectedCams, cameras.length, loadRecs, indexRetry, loadFromSec, appliedRange]);

  // A change of camera or date is a fresh attempt, not a continuation of a
  // stalled one, so the retry budget starts over. Keyed on the query inputs
  // only — never on indexRetry, or a retry would keep resetting its own budget.
  useEffect(() => {
    indexTriesRef.current = 0;
    // Drop the current segments too. The auto-seek effect below bails when
    // currentSegs is non-empty, so without this a changed camera selection
    // never re-seeks: the newly added tiles get no segment and sit on "No
    // recording at this time" until the user presses Go, which calls
    // seekToTime directly and bypasses that guard.
    setCurrentSegs([]);
    // Mark loading immediately, synchronously with the currentSegs reset above
    // — not just once the debounced load effect fires SELECTION_DEBOUNCE_MS
    // later. Without this, "No footage to play" (noPlayback, driven purely by
    // currentSegs.length) could render truthfully-but-wrongly during that gap:
    // currentSegs is already empty, loading hasn't flipped true yet, so the
    // cell claims certainty about a fetch that hasn't even started.
    if (token && selectedCams.size) setLoading(true);
  }, [token, viewDate, selectedCams, cameras.length, loadFromSec]);

  // A different day starts its list at the chosen time again, not at whatever
  // an earlier day was widened to.
  const firstDateRef = useRef(true);
  useEffect(() => {
    if (firstDateRef.current) { firstDateRef.current = false; return; }
    setLoadFromSec(windowStart(hhmmToSecs(viewTime)));
    setViewFromSec(windowStart(hhmmToSecs(viewTime)));
  }, [viewDate]); // eslint-disable-line

  // Abort any in-flight load, and drop any pending retry, on unmount.
  useEffect(() => () => {
    loadAbortRef.current?.abort();
    clearTimeout(indexRetryRef.current);
  }, []);

  // ── visible cameras (only those the customer selected) ────────────────────
  const visibleCams = cameras.filter(c => selectedCams.has(c.idx));

  // Keep the selection valid if the viewport crosses into mobile (e.g.
  // rotation) while more than one camera was already selected.
  useEffect(() => {
    if (!isMobile) return;
    setSelectedCams(prev => prev.size > 1 ? new Set([[...prev][0]]) : prev);
  }, [isMobile]); // eslint-disable-line

  // With exactly one camera visible there's nothing to choose, so pick it
  // automatically; with several, require an explicit tile check and drop the
  // previous pick if that camera is no longer visible.
  const visibleIdxKey = visibleCams.map(c => c.idx).join(',');
  useEffect(() => {
    if (visibleCams.length === 1) { setExportCamIdx(visibleCams[0].idx); return; }
    setExportCamIdx(prev => visibleCams.some(c => c.idx === prev) ? prev : null);
  }, [visibleIdxKey]); // eslint-disable-line

  // CamMultiSelect now owns all the tick/untick/select-all/clear interaction
  // (staging it as a local draft on desktop per PRVSK-38, applying
  // immediately on mobile per PRVSK-37) — this is just where the confirmed
  // result lands, which is also what the recordings-load effect above
  // watches. The MAX_CONCURRENT_PLAYBACK cap is enforced there too.

  // ── Date navigation ───────────────────────────────────────────────────────
  const navigateDate = (delta) => {
    const d = new Date(viewDate + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    const newDate = fmtLocalDate(d);
    if (newDate > today) return;
    setCurrentSegs([]); setPlaybackPos(null);
    setViewDate(newDate);
  };

  // ── Recordings for the selected date (all cameras) ─────────────────────────
  const dayRecs = recs
    .filter(r => dateFromRec(r) === viewDate.replace(/-/g, ''))
    .sort((a, b) => recStartSecs(a) - recStartSecs(b));

  // What the timeline shows: from the chosen chunk, never past the playhead's
  // chunk (e.g. playback snapped back to earlier footage) or before what's loaded.
  const shownFrom = Math.max(loadFromSec, Math.min(viewFromSec,
    playbackPos != null ? windowStart(Math.floor(playbackPos)) : viewFromSec));
  const timelineRecs = dayRecs.filter(r => recStartSecs(r) >= shownFrom);
  // What an empty timeline says — for a range, the range itself (PRVSK-50).
  const emptyMsg = appliedRange
    ? `No recordings between ${fmtSecs(appliedRange.from).slice(0, 5)} and ${fmtSecs(appliedRange.to).slice(0, 5)} on ${fmtDateNav(viewDate)}`
    : shownFrom > 0 ? `No recordings from ${fmtSecs(shownFrom).slice(0, 5)} — tap earlier on the timeline to see more`
    : `No recordings for ${fmtDateNav(viewDate)}`;

  // ── Seek all visible cameras to a specific second-of-day ──────────────────
  const seekToTime = useCallback((seconds, allRecs, cams) => {
    const buildSegs = sec => cams.map(cam => {
      const camRecs = allRecs
        .filter(r => r.cam_idx === cam.idx)
        .sort((a, b) => recStartSecs(a) - recStartSecs(b));
      const rec = camRecs.find((r, i) => {
        const start = recStartSecs(r);
        const end   = i < camRecs.length - 1 ? recStartSecs(camRecs[i+1]) : start + 15 * 60;
        return sec >= start && sec < end;
      });
      return rec ? { cam, rec, offset: Math.max(0, sec - recStartSecs(rec)) } : null;
    }).filter(Boolean);

    let segs = buildSegs(seconds);
    let pos  = seconds;

    // Nothing at the requested time → snap to the next footage at/after it,
    // else fall back to the first available footage of the day.
    if (segs.length === 0) {
      const camIdxs = new Set(cams.map(c => c.idx));
      const starts  = allRecs.filter(r => camIdxs.has(r.cam_idx)).map(recStartSecs).sort((a, b) => a - b);
      if (starts.length) {
        const next = starts.find(s => s >= seconds);
        pos  = next != null ? next : starts[0];
        segs = buildSegs(pos);
      }
    }

    setCurrentSegs(segs);
    setPlaybackPos(pos);
  }, []);

  // Seek, first loading further back if the time is before the loaded part.
  // Widening the window resets the current segments, so the auto-start effect
  // plays from viewTime once the wider list arrives.
  const pendingSeekRef = useRef(null);   // exact target while the wider list loads
  // refocus: an explicitly chosen time (Go / ‹ ›) moves the timeline to it;
  // a tap on the timeline only ever reveals earlier chunks, never hides any.
  const requestSeek = (sec, refocus = false) => {
    timeDirtyRef.current = false;          // any explicit seek applies/discards a pending pick
    const chunk = windowStart(sec);
    if (refocus || chunk < viewFromSec) setViewFromSec(chunk);
    if (sec < loadFromSec) {
      pendingSeekRef.current = sec;
      setViewTime(`${pad(Math.floor(sec / 3600))}:${pad(Math.floor((sec % 3600) / 60))}`);
      setLoadFromSec(windowStart(sec));
      return;
    }
    seekToTime(sec, dayRecs, visibleCams);
  };

  // ── "Go" button — play selected cameras from the chosen date/time ──────────
  const rangeStart = timeToSecs(viewTime);
  const rangeEnd   = timeToSecs(viewToTime);
  const rangeErr   = !rangeOn ? null
    : rangeEnd == null ? 'Pick an end time'
    : rangeStart != null && rangeEnd <= rangeStart ? 'End must be after the start (one day at a time)'
    : rangeStart != null && rangeEnd - rangeStart > MAX_RANGE_SECS ? `At most ${MAX_RANGE_SECS / 3600} hours at a time`
    : null;

  const handleGoToTime = () => {
    if (!viewTime || visibleCams.length === 0) return;
    const [h, m] = viewTime.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return;
    if (rangeOn) {
      if (rangeErr) return;
      setAppliedRange({ from: h * 3600 + m * 60, to: rangeEnd });
    } else if (appliedRange) {
      setAppliedRange(null);
    }
    requestSeek(h * 3600 + m * 60, true);
  };

  const openRange = () => {
    // Default end: an hour after the start, inside the day.
    const st = timeToSecs(viewTime) ?? 0;
    const en = Math.min(86340, st + 3600);
    setViewToTime(`${pad(Math.floor(en / 3600))}:${pad(Math.floor((en % 3600) / 60))}`);
    setRangeOn(true);
  };
  const closeRange = () => { setRangeOn(false); setAppliedRange(null); };

  // ── Time step arrows (like the date arrows) — nudge by ±15 min ────────────
  const TIME_STEP_MIN = 15;
  const stepTime = (deltaMin) => {
    const [h, m] = (viewTime || '00:00').split(':').map(Number);
    const total  = Math.max(0, Math.min(86399, (h * 3600 + m * 60) + deltaMin * 60));
    if (rangeOn) {
      // A range moves as a whole window: start and end together.
      const en = rangeEnd != null ? Math.max(0, Math.min(86340, rangeEnd + deltaMin * 60)) : null;
      setViewTime(`${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}`);
      if (en != null) setViewToTime(`${pad(Math.floor(en / 3600))}:${pad(Math.floor((en % 3600) / 60))}`);
      if (visibleCams.length > 0 && en != null && en > total && en - total <= MAX_RANGE_SECS) {
        setAppliedRange({ from: total, to: en });
        requestSeek(total, true);
      }
      return;
    }
    if (visibleCams.length > 0) {
      requestSeek(total, true);                  // jump playback; time field follows via sync
    } else {
      setViewTime(`${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}`);
    }
  };

  // ── Auto-advance to the next chunk when a segment finishes ────────────────
  const handleChunkEnded = (seg) => {
    if (!seg) return;
    const now = Date.now();
    if (now - lastAdvanceRef.current < 2000) return;   // dedupe multiple cameras ending together
    const nextSec  = recStartSecs(seg.rec) + 15 * 60;
    if (nextSec >= 86400) return;                      // end of day
    const camIdxs  = new Set(visibleCams.map(c => c.idx));
    const upcoming = dayRecs
      .filter(r => camIdxs.has(r.cam_idx))
      .map(recStartSecs)
      .filter(s => s >= nextSec)
      .sort((a, b) => a - b);
    if (!upcoming.length) return;                      // no more footage → stop (don't loop)
    lastAdvanceRef.current = now;
    seekToTime(upcoming[0], dayRecs, visibleCams);     // jump to the next available chunk
  };

  // ── Auto-start playback when a fresh recordings list loads (no Go needed) ──
  useEffect(() => {
    if (loading || !visibleCams.length || !dayRecs.length || currentSegs.length) return;
    const [h, m] = (viewTime || '00:00').split(':').map(Number);
    const target = pendingSeekRef.current ?? h * 3600 + m * 60;
    pendingSeekRef.current = null;
    timeDirtyRef.current = false;          // the picked time is what's playing now
    seekToTime(target, dayRecs, visibleCams);
    // selectedCams included so adding/removing a camera re-seeks the new set
    // rather than waiting for a fresh recordings load.
  }, [recs, loading, selectedCams]); // eslint-disable-line

  // ── Recover gapped cameras as the clock advances ──────────────────────────
  // A camera seeked into a gap shows "No recording" and never re-evaluates on
  // its own while the others keep playing. As playbackPos moves, give any tile
  // without a segment one once its footage starts — without touching the tiles
  // already playing (same seg object → no remount).
  useEffect(() => {
    if (playbackPos == null || !visibleCams.length) return;
    setCurrentSegs(prev => {
      let changed = false;
      const next = visibleCams.map(cam => {
        const existing = prev.find(s => s.cam.idx === cam.idx);
        if (existing) return existing;                 // already playing — leave untouched
        const camRecs = dayRecs
          .filter(r => r.cam_idx === cam.idx)
          .sort((a, b) => recStartSecs(a) - recStartSecs(b));
        const rec = camRecs.find((r, i) => {
          const start = recStartSecs(r);
          const end   = i < camRecs.length - 1 ? recStartSecs(camRecs[i + 1]) : start + 15 * 60;
          return playbackPos >= start && playbackPos < end;
        });
        if (rec) { changed = true; return { cam, rec, offset: Math.max(0, playbackPos - recStartSecs(rec)) }; }
        return null;
      }).filter(Boolean);
      return changed ? next : prev;
    });
  }, [playbackPos]); // eslint-disable-line

  const isHls    = rec => rec?.type === 'hls';
  const videoUrl = rec => STREAM_API && rec?.url ? `${STREAM_API}${rec.url}?token=${token}` : '';
  const cols     = visibleCams.length <= 1 ? 1 : visibleCams.length === 2 ? 2 : visibleCams.length <= 4 ? 2 : 3;

  // ── Clip export: mark in/out on the timeline, then download the .mp4 ──
  const clipDur   = (clipIn != null && clipOut != null && clipOut > clipIn) ? (clipOut - clipIn) : 0;  // secs
  const clipOver  = clipDur > exportMax * 60;
  const clipErr   = clipIn != null && clipOut != null && clipOut <= clipIn ? 'End must be after the start.' : null;
  // Typed In/Out (PRVSK-78 #2): a time box beside each Mark button. Marking fills
  // it from the playhead; typing sets it without scrubbing. "" clears it.
  const setClipFromInput = (which, v) => {
    const sec = v ? timeToSecs(v) : null;
    if (v && sec == null) return;           // half-typed — wait for a full time
    (which === 'in' ? setClipIn : setClipOut)(sec);
  };
  // The camera the checked tile picked — null when >1 camera is visible and
  // none has been explicitly checked yet.
  const exportCam = visibleCams.find(c => c.idx === exportCamIdx) || null;
  const dtStamp   = s => { const sec = Math.floor(s); return `${viewDate.replace(/-/g, '')}_${pad(Math.floor(sec/3600))}${pad(Math.floor((sec%3600)/60))}${pad(sec%60)}`; };
  const exportClip = async () => {
    const cam = exportCam;
    if (!cam || clipIn == null || clipOut == null || clipDur <= 0 || clipOver) return;
    setExporting(true);
    setExportErr(null);
    const url = `${STREAM_API}/recordings/export?cam_idx=${cam.idx}` +
                `&from_dt=${dtStamp(clipIn)}&to_dt=${dtStamp(clipOut)}&token=${token}`;
    const name = `${cam.name}_${dtStamp(clipIn)}_to_${dtStamp(clipOut)}.mp4`
                   .replace(/[^\w.-]+/g, '_');
    try {
      // Never point the browser AT the URL. iOS ignores <a download> and
      // navigates instead, so inside the WebView app the page was replaced by
      // the OS file preview with no chrome and no way back — the app had to be
      // force-closed. Fetching the bytes keeps the page alive either way.
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const file = new File([blob], name, { type: 'video/mp4' });
      if (navigator.canShare?.({ files: [file] })) {
        // Native share sheet: dismissible, and returns to the app.
        await navigator.share({ files: [file], title: name });
      } else {
        const obj = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = obj; a.download = name;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(obj), 60_000);
      }
    } catch (e) {
      // The user dismissing the share sheet is a cancel, not a failure.
      if (e?.name !== 'AbortError') setExportErr(e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const toggleFs = () => {
    if (isFs) {
      setIsFs(false);
      exitFullscreen();
    } else {
      setIsFs(true);
      requestFullscreen();        // best-effort: absent on iOS Safari
    }
  };

  // Sync isFs when user presses Escape / leaves fullscreen from system UI
  useEffect(() => onFullscreenExit(() => setIsFs(false)), []);

  if (!initDone) return (
    <div className="rec-page"><div className="rec-init-screen"><div className="rec-spinner" />Connecting to server…</div></div>
  );

  const noPlayback = currentSegs.length === 0;

  return (
    <div className="rec-page">

      {/* ── Playback filter bar ── */}
      <div className={`rec-filter-topbar${rangeOn ? ' is-range' : ''}`}>

        {/* Date navigation */}
        <div className="rec-date-nav">
          <span className="rec-time-filter-label">Date</span>
          <button className="rec-date-nav-btn" onClick={() => navigateDate(-1)}>‹</button>
          <input
            type="date"
            value={viewDate}
            max={today}
            onChange={e => {
              const d = e.target.value;
              if (!d || d > today) return;
              setCurrentSegs([]); setPlaybackPos(null);
              setViewDate(d);
            }}
            className="rec-dt-input"
          />
          <button className="rec-date-nav-btn" onClick={() => navigateDate(1)} disabled={isToday}>›</button>
        </div>

        <div className="rec-filter-topbar-divider" />

        {/* Time */}
        <div className={`rec-time-filter${rangeOn ? ' is-range' : ''}`}>
          <span className="rec-time-filter-label">Time</span>
          <button className="rec-date-nav-btn" onClick={() => stepTime(-TIME_STEP_MIN)} title={`Back ${TIME_STEP_MIN} min`}>‹</button>
          <input
            type="time"
            value={viewTime}
            onChange={e => { setViewTime(e.target.value); timeDirtyRef.current = true; }}
            onKeyDown={e => { if (e.key === 'Enter') handleGoToTime(); }}
            onFocus={() => { timeEditingRef.current = true; }}
            onBlur={() => { timeEditingRef.current = false; }}
            className="rec-dt-input"
            style={{ width: 110 }}
            aria-label={rangeOn ? 'Start time' : 'Time'}
          />
          {rangeOn && (
            <>
              <span className="rec-range-sep" aria-hidden="true">→</span>
              <input
                type="time"
                value={viewToTime}
                onChange={e => setViewToTime(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleGoToTime(); }}
                className={`rec-dt-input${rangeErr && viewToTime ? ' is-bad' : ''}`}
                style={{ width: 110 }}
                aria-label="End time"
                title={`End time — up to ${MAX_RANGE_SECS / 3600} hours after the start`}
              />
            </>
          )}
          <button className="rec-date-nav-btn" onClick={() => stepTime(TIME_STEP_MIN)} title={`Forward ${TIME_STEP_MIN} min`}>›</button>
          {rangeOn
            ? <button type="button" className="rec-range-link" onClick={closeRange} title="Back to one time" aria-label="Back to one time">✕</button>
            : <button type="button" className="rec-range-link rec-range-open" onClick={openRange}
                      title="See a start → end window, e.g. 18:30 to 20:00" aria-label="Add an end time">
                {/* Full label on desktop; on a phone row 1 is shared with the date,
                    so it shrinks to a round "+" like the ‹ › buttons beside it. */}
                <span className="rec-range-open-full">+ end time</span>
                <span className="rec-range-open-short" aria-hidden="true">+</span>
              </button>}
          {rangeOn && rangeErr && viewToTime && <span className="rec-range-err" role="alert">{rangeErr}</span>}
        </div>

        {/* Camera multi-select — sits just left of Go */}
        {cameras.length > 0 && (
          <CamMultiSelect
            cameras={cameras}
            selected={selectedCams}
            onApply={setSelectedCams}
            max={MAX_CONCURRENT_PLAYBACK}
            singleSelect={isMobile}
          />
        )}

        {/* Go — last step, after picking time + camera */}
        <button
          className="rec-apply-btn"
          onClick={handleGoToTime}
          disabled={!viewTime || loading || visibleCams.length === 0 || !!rangeErr}
          title={visibleCams.length === 0 ? 'Select a camera first' : rangeErr || (rangeOn ? 'Load this time range' : 'Play from this date/time')}
        >
          Go ▶
        </button>

        {/* Right: playback clock + fullscreen */}
        <div className="rec-filter-topbar-right">
          {playbackPos != null && (
            <span className="rec-playback-clock">{fmtSecs(playbackPos)}</span>
          )}
          <button className="rec-wall-btn" onClick={toggleFs} title="Wall fullscreen">⛶</button>
        </div>
      </div>

      {/* ── Timeline — second row, directly under the header ── */}
      {/* Hidden until a camera is selected — otherwise the scrubber and
          Mark/Export controls look active with nothing to act on. */}
      {!isFs && visibleCams.length > 0 && (
        <div className="rec-timeline-footer rec-timeline-top">
          <TimelineBar
            recs={timelineRecs}
            onSeek={sec => requestSeek(sec)}
            loadedFrom={shownFrom}
            nowSec={isToday ? nowSecOfDay() : null}
            currentPos={playbackPos}
            inSec={clipIn}
            outSec={clipOut}
            rangeFrom={appliedRange?.from ?? null}
            rangeTo={appliedRange?.to ?? null}
            emptyLabel={!loading && visibleCams.length > 0 && timelineRecs.length === 0
              ? emptyMsg
              : null}
          />
          {/* Clip export controls — scrub the timeline, mark In/Out, download.
              .rec-clip-area gives them the timeline's side margin. */}
          <div className="rec-clip-area">
          <div className="rec-clip-bar">
            <button className="rec-wall-btn rec-clip-mark" disabled={playbackPos == null}
              onClick={() => { const p = Math.floor(playbackPos); setClipIn(p); if (clipOut != null && clipOut <= p) setClipOut(null); }}
              title="Set clip start at the playhead">⟤ Mark In</button>
            <button className="rec-wall-btn rec-clip-mark" disabled={playbackPos == null || clipIn == null || Math.floor(playbackPos) <= clipIn}
              onClick={() => setClipOut(Math.floor(playbackPos))} title="Set clip end at the playhead">Mark Out ⟥</button>
            <span className="rec-clip-times">
              <label className="rec-clip-time">In
                <input type="time" step="1" className="rec-dt-input is-in"
                  value={clipIn != null ? fmtSecs(clipIn) : ''}
                  onChange={e => setClipFromInput('in', e.target.value)}
                  aria-label="Clip start" title="Clip start — type a time or press Mark In" />
              </label>
              <span className="rec-range-sep" aria-hidden="true">→</span>
              <label className="rec-clip-time">Out
                <input type="time" step="1" className={`rec-dt-input is-out${clipErr ? ' is-bad' : ''}`}
                  value={clipOut != null ? fmtSecs(clipOut) : ''}
                  onChange={e => setClipFromInput('out', e.target.value)}
                  aria-label="Clip end" title="Clip end — type a time or press Mark Out" />
              </label>
              {clipDur > 0 && (
                <b style={{ color: clipOver ? '#e74c3c' : 'inherit' }}>{Math.floor(clipDur/60)}m {clipDur%60}s</b>
              )}
            </span>
            {(clipIn != null || clipOut != null) && (
              <button className="rec-wall-btn rec-clip-clear" onClick={() => { setClipIn(null); setClipOut(null); }}>Clear</button>
            )}
            <button className="rec-wall-btn rec-clip-export" style={{ fontWeight:700,
                background: (clipDur > 0 && !clipOver && exportCam) ? 'var(--ind-accent,#3b82f6)' : undefined,
                color: (clipDur > 0 && !clipOver && exportCam) ? '#fff' : undefined }}
              disabled={clipDur <= 0 || clipOver || !exportCam || exporting}
              onClick={exportClip}
              title={clipOver ? `Over the ${exportMax} min limit` : !exportCam ? 'Check a camera\'s tile above to choose which one to export' : `Export ${exportCam.name}`}>
              {exporting ? 'Preparing…' : '⤓ Export clip'}
            </button>
          </div>
          {clipErr && <div style={{ fontSize:12, color:'#e74c3c', marginTop:4 }} role="alert">{clipErr}</div>}
          {clipOver && <div style={{ fontSize:12, color:'#e74c3c', marginTop:4 }}>Clip exceeds the {exportMax}-minute export limit — shorten the selection.</div>}
          {exportErr && <div style={{ fontSize:12, color:'#e74c3c', marginTop:4 }}>{exportErr}</div>}
          {visibleCams.length > 1 && !exportCam && clipDur > 0 && !clipOver && (
            <div style={{ fontSize:12, color:'#f39c12', marginTop:4 }}>⚠ Check a camera's tile above to choose which one to export.</div>
          )}
          {visibleCams.length > 1 && exportCam && clipDur > 0 && !clipOver && (
            <div style={{ fontSize:12, color:'var(--ind-tx3,#8a94a6)', marginTop:4 }}>Exporting <b>{exportCam.name}</b>.</div>
          )}
          </div>
        </div>
      )}

      {/* ── All-cameras grid (scrollable) — hidden when fullscreen (rendered in portal) ── */}
      {!isFs && (
        <div className="rec-grid-scroll">
          {indexing === 'stalled' ? (
            <div className="rec-grid-loading">
              <div>
                <div>Recordings index is not coming up.</div>
                <div className="rec-grid-loading-sub">
                  The server kept reporting it as still building. Check that the
                  recordings folder exists and is readable, then reload.
                </div>
              </div>
            </div>
          ) : indexing ? (
            <div className="rec-grid-loading">
              <div className="rec-spinner" />
              <div>
                <div>Indexing recordings…</div>
                <div className="rec-grid-loading-sub">
                  This runs once after a restart. Playback is instant afterwards.
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="rec-grid-loading"><div className="rec-spinner" />Loading recordings…</div>
          ) : visibleCams.length === 0 ? (
            <div className="rec-cam-empty-select">
              <div className="rec-cam-empty-icon">📷</div>
              <div className="rec-cam-empty-title">Select a camera to view its recording</div>
              <div className="rec-cam-empty-hint">Use the camera menu above to choose one or more cameras.</div>
            </div>
          ) : (
            <div className="rec-cam-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
              {visibleCams.map(cam => {
                const seg = currentSegs.find(s => s.cam.idx === cam.idx);
                return <RecCamCell key={cam.id} cam={cam} seg={seg}
                  token={token} noPlayback={noPlayback}
                  isHls={isHls} videoUrl={videoUrl}
                  speed={playSpeed} onSpeed={setPlaySpeed}
                  onTimeUpdate={e => setPlaybackPos(recStartSecs(seg?.rec) + e.target.currentTime)}
                  onEnded={() => handleChunkEnded(seg)}
                  showExportPick={visibleCams.length > 1}
                  isExportPick={exportCamIdx === cam.idx}
                  onPickExport={setExportCamIdx}
                />;
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Fullscreen portal (grid + timeline) ── */}
      {isFs && createPortal(
        <div className="rec-wall-overlay">
          <div className="rec-grid-scroll">
            {loading ? (
              <div className="rec-grid-loading"><div className="rec-spinner" />Loading recordings…</div>
            ) : visibleCams.length === 0 ? (
              <div className="rec-cam-empty-select">
                <div className="rec-cam-empty-icon">📷</div>
                <div>Select one or more cameras to view</div>
              </div>
            ) : (
              <div className="rec-cam-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
                {visibleCams.map(cam => {
                  const seg = currentSegs.find(s => s.cam.idx === cam.idx);
                  return <RecCamCell key={cam.id} cam={cam} seg={seg}
                    token={token} noPlayback={noPlayback}
                    isHls={isHls} videoUrl={videoUrl}
                    speed={playSpeed} onSpeed={setPlaySpeed}
                    onTimeUpdate={e => setPlaybackPos(recStartSecs(seg?.rec) + e.target.currentTime)}
                    onEnded={() => handleChunkEnded(seg)}
                  />;
                })}
              </div>
            )}
          </div>
          <div className="rec-timeline-footer">
            <TimelineBar
              recs={timelineRecs}
              onSeek={sec => requestSeek(sec)}
              loadedFrom={shownFrom}
              nowSec={isToday ? nowSecOfDay() : null}
              currentPos={playbackPos}
              rangeFrom={appliedRange?.from ?? null}
              rangeTo={appliedRange?.to ?? null}
              emptyLabel={!loading && visibleCams.length > 0 && timelineRecs.length === 0
              ? emptyMsg
              : null}
            />
          </div>
          <button className="rec-wall-exit-btn" onClick={toggleFs}>✕ Exit Fullscreen</button>
        </div>,
        document.body
      )}
    </div>
  );
}
